/* eslint-disable  @typescript-eslint/no-explicit-any */
import { Client, ReconnectionTimeMode, StompConfig, StompSubscription } from '@stomp/stompjs';

/**
 * Internal type for holding a subscription
 */
type Subscription<T> = {
	clientSubscription?: StompSubscription;
	ref: string;
	route: string;
	onSubscribed: () => void;
	onMessage: (message: T) => void;
	onError: (message: string, error: Error) => void;
	cancel?: () => void;
};

/**
 * Holds a websocket connection to sbbet
 * Keeps track of open subscriptions and ensures retries and refetching when reconnecting
 */
export default class STOMPClient {
	private baseUrl: string;

	private client: Client | null = null;

	// Array of all open subscriptions, will be used when subscribing and connecting
	private subscriptions = new Map<string, Subscription<any>>();

	// When establishing the actual connection, so we don't accidentally opens two connections
	private isConnecting = false;

	private bettingSessionExpiresOn: string | null;

	constructor(baseUrl: string) {
		this.baseUrl = baseUrl;
		this.bettingSessionExpiresOn = null;
	}

	public getBaseUrl = () => this.baseUrl;

	public getSubscriptions = () => this.subscriptions;

	/**
	 * Connect to websocket, must be called before subscribing
	 */
	public connect = async () => {
		if (this.isConnecting || this.client) {
			return;
		}

		// Create the STOMP client
		const stompConfig: StompConfig = {
			brokerURL: this.baseUrl,
			heartbeatIncoming: 20000, // Should preferrably be the same as on server
			heartbeatOutgoing: 20000, // Should preferrably be the same as on server
			reconnectDelay: 1000,
			maxReconnectDelay: 30000,
			reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL
		};
		this.client = new Client(stompConfig);
		this.client.onConnect = () => {
			this.isConnecting = false;
			this.handleNewConnection();
		};
		this.client.onWebSocketError = error => {
			console.error(error);
		};
		this.client.onStompError = frame => {
			console.error(frame);
		};

		// Activate the client which will make it connect and automatically try to reconnect
		this.client.activate();
	};

	/**
	 * Disconnect from websocket and unsubscribes to all current subscriptions
	 */
	public disconnect = () => {
		if (this.client) {
			try {
				this.subscriptions.forEach((_, ref) => this.unsubscribe(ref));
				this.client.deactivate({ force: true }).catch(reason => {
					console.error(reason);
				});
			} catch {
				// Do nothing
			}
		}

		this.client = null;
		this.subscriptions.clear();
	};

	public updateBettingSessionExpiration = (bettingSessionExpiresOn: string | null) => {
		this.bettingSessionExpiresOn = bettingSessionExpiresOn;
	};

	/**
	 * Start subscribing to provided subscription
	 * @param subscription Subscription to be subscribed to
	 */
	public subscribe = (subscription: Subscription<any>) => {
		if (this.subscriptions.has(subscription.ref)) {
			return;
		}

		this.subscriptions.set(subscription.ref, subscription);

		if (!this.client) {
			// Consumer has not called connect() yet or we are currently trying to re/connect
			// The re/connect logic will re/subscribe once a connection has been established
			// So we don't need to do anything more here
			return;
		}

		this.subscribeToSTOMP(subscription.route, subscription.ref);
	};

	/**
	 * Completely unsubscribes from a reference
	 * @param ref Subscription reference
	 */
	public unsubscribe = (ref: string) => {
		if (!this.subscriptions.has(ref)) {
			return;
		}

		const subscription = this.subscriptions.get(ref);
		if (subscription?.cancel) {
			try {
				subscription.cancel();
			} catch {
				// Do nothing if we can't cancel
			}
		}

		this.subscriptions.delete(ref);
	};

	private handleNewConnection = () => {
		this.subscriptions.forEach(({ route }, ref) => {
			this.subscribeToSTOMP(route, ref);
		});
	};

	private subscribeToSTOMP = (route: string, ref: string) => {
		if (!this.client || !this.client?.connected || !this.subscriptions.has(ref)) {
			return;
		}

		const { onSubscribed, onMessage, onError } = <Subscription<any>>this.subscriptions.get(ref);

		if (this.bettingSessionExpiresOn != null && new Date(this.bettingSessionExpiresOn) < new Date()) {
			return;
		}

		try {
			const clientSubscription = this.client.subscribe(route, msg => {
				// TODO Fix conversion
				// TODO Handle errors when parsing message (what if data is null etc)
				const response = <unknown>msg.body;

				// TODO Fix conversion
				if (typeof response === 'string') {
					onMessage(JSON.parse(response));
				} else if (response instanceof Uint8Array || response instanceof ArrayBuffer) {
					const text = new TextDecoder().decode(response);
					onMessage(JSON.parse(text));
				} else {
					onMessage(response);
				}
			});
			const subscription = <Subscription<any>>this.subscriptions.get(ref);
			this.subscriptions.set(ref, {
				...subscription,
				clientSubscription,
				cancel: () => {
					clientSubscription?.unsubscribe();
				}
			});
			onSubscribed();
		} catch (e) {
			if (e instanceof Error) {
				onError('Could not subscribe: stomp failure', e);
			}
			console.error(e);
		}
	};
}

// Singleton
let stompClient: STOMPClient | null = null;

export const createClient = (websocketUrl: string) => {
	stompClient = new STOMPClient(websocketUrl);
};

export const getSTOMPClient = () => {
	if (stompClient == null) {
		throw new Error('Sportsbook betting stomp client has not been configured');
	}
	return stompClient;
};

// HMR for Singleton
// add the old client when disposing the module
// and then create a new one with the same settings and subscriptions
if (import.meta && import.meta.webpackHot) {
	import.meta.webpackHot.dispose(data => {
		// eslint-disable-next-line
		data.stompClient = stompClient;
	});

	// eslint-disable-next-line
	if (import.meta.webpackHot.data?.stompClient) {
		const oldClient = <
			STOMPClient // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		>import.meta.webpackHot.data.stompClient;
		createClient(oldClient.getBaseUrl());
		const oldSubscriptions = oldClient.getSubscriptions();

		oldSubscriptions.forEach((subscription, ref) => {
			getSTOMPClient().subscribe({
				ref,
				route: subscription.route,
				onSubscribed: subscription.onSubscribed,
				onMessage: subscription.onMessage,
				onError: subscription.onError
			});
		});
		oldClient.disconnect();
		getSTOMPClient()
			.connect()
			.then(() => {
				// Do Nothing
			})
			.catch(reason => {
				console.error(reason);
			});
	}
}
